import React, { useMemo, useState } from "react";
import { CaretUp, CaretDown, ArrowsDownUp } from "@phosphor-icons/react";

/**
 * Ortak sortable table header bileşeni.
 * Kullanım:
 *   <SortableTh label="Ad" k="name" sort={sort} onSort={toggle} className="px-3 py-2 text-left" />
 */
export function SortableTh({
  label, k, sort, onSort, className = "", align = "left", tone = "light",
  children,
}) {
  const active = sort?.key === k;
  const dir = active ? sort.dir : null;
  const colors = tone === "dark"
    ? { active: "text-white", idle: "text-white/70", hover: "hover:text-white" }
    : { active: "text-[#1B4776]", idle: "text-slate-500", hover: "hover:text-[#1B4776]" };
  return (
    <th className={className}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={`inline-flex items-center gap-1 uppercase tracking-widest font-bold ${
          active ? colors.active : colors.idle
        } ${colors.hover}`}
        style={{
          justifyContent: align === "right" ? "flex-end"
                        : align === "center" ? "center" : "flex-start",
          width: "100%",
        }}
      >
        {label || children}
        {dir === "asc" && <CaretUp size={10} weight="bold" />}
        {dir === "desc" && <CaretDown size={10} weight="bold" />}
        {!active && <ArrowsDownUp size={10} weight="bold" className="opacity-40" />}
      </button>
    </th>
  );
}

// Türkçe uyumlu alfabetik normalize (aksan kaldır + lowercase)
export const normTR = (s) => (s || "").toString().trim().toLowerCase()
  .replace(/ç/g, "c").replace(/ğ/g, "g").replace(/ı/g, "i")
  .replace(/İ/g, "i").replace(/ö/g, "o").replace(/ş/g, "s").replace(/ü/g, "u");

/**
 * useSortableRows(rows, options)
 *
 * options.defaultKey: default sort key
 * options.defaultDir: 'asc' | 'desc'
 * options.getters: { [key]: (row) => value }  — özel sütun getters
 *
 * Dönenler: { sort, toggleSort, sortedRows }
 */
export function useSortableRows(rows, { defaultKey = "", defaultDir = "asc", getters = {} } = {}) {
  const [sort, setSort] = useState({ key: defaultKey, dir: defaultDir });
  const toggleSort = (key) => setSort((s) =>
    s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
  );
  const sortedRows = useMemo(() => {
    if (!sort.key) return rows;
    const getter = getters[sort.key] || ((r) => r?.[sort.key]);
    const arr = [...(rows || [])];
    arr.sort((a, b) => {
      let va = getter(a);
      let vb = getter(b);
      // Sayısalsa direkt karşılaştır
      if (typeof va === "number" || typeof vb === "number") {
        va = Number(va) || 0; vb = Number(vb) || 0;
      } else {
        va = normTR(va);
        vb = normTR(vb);
      }
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sort, getters]);
  return { sort, toggleSort, sortedRows };
}

export default SortableTh;
